import {useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';

import {
  enableCalendarAfterRedirection,
  getAllCalendarDays,
  getMeetingPreferencesLoadingStatus,
  setIsSigningOut,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {useAppDispatch, useAppSelector, useIsTeamsApp} from '@hooks';
import {app, authentication, FrameContexts} from '@microsoft/teams-js';

export const useDetectRedirect = () => {
  const isTeamsApp = useIsTeamsApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const meetingPreferencesLoadingStatus = useAppSelector(getMeetingPreferencesLoadingStatus);
  const days = useAppSelector((state) => getAllCalendarDays(state));

  const redirectSource = searchParams.get('redirect_source');
  const result = searchParams.get('result');

  useEffect(() => {
    if (!isTeamsApp || redirectSource !== 'meet_consent') return;
    dispatch(setIsSigningOut(true)); // force spinner to not show login page

    const sendResultToMainApp = async () => {
      try {
        await app.initialize();
        await app.notifySuccess();

        const context = await app.getContext();
        if (context.page.frameContext !== FrameContexts.authentication) return; // only handle when in popup

        authentication.notifySuccess(result ?? '');
      } catch (error) {
        authentication.notifyFailure(result ?? '');
      }
    };

    sendResultToMainApp();
  }, [dispatch, isTeamsApp, redirectSource, result]);

  useEffect(() => {
    if (isTeamsApp) return;

    /**
     * * Logic for redirect flow for calendar consent
     */
    if (redirectSource === 'meet_consent' && meetingPreferencesLoadingStatus === 'Loaded') {
      if (result === 'success') {
        const calendarConsentSuccessful = async () => {
          await dispatch(withAsyncThunkErrorHandling(() => enableCalendarAfterRedirection()));
          setSearchParams();
        };

        calendarConsentSuccessful();
      } else {
        setSearchParams();
      }
    }
  }, [days, dispatch, meetingPreferencesLoadingStatus, setSearchParams, redirectSource, result, isTeamsApp]);
};
